import dayjs from "dayjs";
import { enUS, ko } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import type { ReactNode } from "react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { Calendar, CalendarDayButton } from "../../primitive/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitive/popover";
import { Button } from "../button/button";
import { Label } from "../label/label";
import { Tooltip, TooltipProvider } from "../tooltip/tooltip";
import {
  createLocalDisplayDate,
  type DateISOString,
  getZonedCalendarDateParts,
  serializeDateSelection,
} from "./date-picker-date";

const DATE_DISPLAY_FORMAT = "MMM D, YYYY";

function formatSelectedDateDisplay(date: Date, locale?: string) {
  if (locale?.toLowerCase().startsWith("ko")) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}년 ${month}월 ${day}일`;
  }

  return dayjs(date).format(DATE_DISPLAY_FORMAT);
}

function resolveCalendarLocale(
  locale?: string,
): React.ComponentProps<typeof Calendar>["locale"] {
  const normalizedLocale = locale?.toLowerCase();

  if (normalizedLocale?.startsWith("ko")) {
    return ko;
  }

  if (normalizedLocale?.startsWith("en")) {
    return enUS;
  }

  return undefined;
}

function transformCalendarMatcherDate(date: Date, timeZone?: string) {
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  if (!timeZone) {
    return date;
  }

  const parts = getZonedCalendarDateParts(date.toISOString(), timeZone);
  return parts ? createLocalDisplayDate(parts) : date;
}

function transformCalendarMatcher(
  matcher: React.ComponentProps<typeof Calendar>["disabled"],
  timeZone?: string,
): React.ComponentProps<typeof Calendar>["disabled"] {
  if (
    !timeZone ||
    matcher == null ||
    typeof matcher === "boolean" ||
    typeof matcher === "function"
  ) {
    return matcher;
  }

  if (matcher instanceof Date) {
    return transformCalendarMatcherDate(matcher, timeZone);
  }

  if (Array.isArray(matcher)) {
    return matcher
      .map((value) => transformCalendarMatcher(value, timeZone))
      .filter((value) => value !== undefined) as React.ComponentProps<
      typeof Calendar
    >["disabled"];
  }

  if (typeof matcher === "object") {
    const nextMatcher = { ...matcher } as Record<string, unknown>;

    ["before", "after", "from", "to"].forEach((key) => {
      const currentValue = nextMatcher[key];

      if (currentValue instanceof Date) {
        nextMatcher[key] = transformCalendarMatcherDate(currentValue, timeZone);
      }
    });

    return nextMatcher as React.ComponentProps<typeof Calendar>["disabled"];
  }

  return matcher;
}

export interface DatePickerProps {
  readonly value?: DateISOString;
  readonly onChange?: (date: DateISOString | undefined) => void;
  readonly today?: DateISOString;
  readonly timeZone?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly disabledDates?: React.ComponentProps<typeof Calendar>["disabled"];
  readonly className?: string;
  readonly displayValue?: ReactNode;
  readonly spacingClassName?: string;
  readonly label?: ReactNode;
  readonly locale?: string;
  readonly ref?: React.Ref<HTMLButtonElement>;
  readonly align?: React.ComponentProps<typeof PopoverContent>["align"];
  readonly triggerClassName?: string;
  readonly getDayTooltip?: (date: Date) => ReactNode;
}

export const DatePicker = ({
  value,
  onChange,
  today,
  timeZone,
  placeholder = "Pick a date",
  disabled = false,
  invalid = false,
  disabledDates,
  className,
  displayValue,
  spacingClassName,
  label,
  locale,
  ref,
  triggerClassName,
  getDayTooltip,
  align = "start",
  ...props
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedDate = React.useMemo(() => {
    const calendarDateParts = getZonedCalendarDateParts(value, timeZone);
    return calendarDateParts
      ? createLocalDisplayDate(calendarDateParts)
      : undefined;
  }, [timeZone, value]);
  const calendarToday = React.useMemo(() => {
    const calendarDateParts = getZonedCalendarDateParts(today, timeZone);
    return calendarDateParts
      ? createLocalDisplayDate(calendarDateParts)
      : undefined;
  }, [timeZone, today]);
  const normalizedDisabledDates = React.useMemo(
    () => transformCalendarMatcher(disabledDates, timeZone),
    [disabledDates, timeZone],
  );
  const calendarLocale = React.useMemo(
    () => resolveCalendarLocale(locale),
    [locale],
  );

  const handleDateSelect = (date: Date | undefined) => {
    onChange?.(serializeDateSelection(date, timeZone));
    setIsOpen(false);
  };
  const calendarComponents = React.useMemo<
    React.ComponentProps<typeof Calendar>["components"] | undefined
  >(() => {
    if (!getDayTooltip) {
      return undefined;
    }

    return {
      DayButton: (dayButtonProps) => {
        const tooltipContent = getDayTooltip(dayButtonProps.day.date);

        if (!tooltipContent) {
          return <CalendarDayButton {...dayButtonProps} />;
        }

        return (
          <Tooltip content={tooltipContent}>
            <span className="inline-flex size-full">
              <CalendarDayButton {...dayButtonProps} />
            </span>
          </Tooltip>
        );
      },
    };
  }, [getDayTooltip]);

  return (
    <div className={cn(spacingClassName ?? "space-y-2", className)}>
      {label != null && <Label>{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className={cn(
              !selectedDate && "text-zinc-500",
              selectedDate && "text-zinc-900",
              triggerClassName,
            )}
            {...props}
          >
            <CalendarIcon className="size-4" />
            {selectedDate
              ? (displayValue ??
                formatSelectedDateDisplay(selectedDate, locale))
              : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align={align}>
          {getDayTooltip ? (
            <TooltipProvider>
              <Calendar
                mode="single"
                components={calendarComponents}
                selected={selectedDate}
                defaultMonth={selectedDate}
                locale={calendarLocale}
                onSelect={handleDateSelect}
                disabled={disabled ? true : normalizedDisabledDates}
                {...(calendarToday ? { today: calendarToday } : {})}
                autoFocus
              />
            </TooltipProvider>
          ) : (
            <Calendar
              mode="single"
              selected={selectedDate}
              defaultMonth={selectedDate}
              locale={calendarLocale}
              onSelect={handleDateSelect}
              disabled={disabled ? true : normalizedDisabledDates}
              {...(calendarToday ? { today: calendarToday } : {})}
              autoFocus
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

DatePicker.displayName = "DatePicker";
